import {
  Box,
  getBoundingBox,
  Point,
  Resolution,
} from "@/features/Editor/Canvas/canvas-geometry";
import type { CanvasElement } from "./canvas-state";

export type AlignType =
  "left" | "center" | "right" | "top" | "middle" | "bottom";

// Used with Alt (Option on Mac), as in Figma.
export const ALIGN_SHORTCUT_KEYS: Record<AlignType, string> = {
  left: "A",
  center: "H",
  right: "D",
  top: "W",
  middle: "V",
  bottom: "S",
};

// A single element aligns to the canvas; several align to each other within their
// selection box.
export function getAlignOffsets(
  elements: CanvasElement[],
  alignType: AlignType,
  resolution: Resolution,
): Record<string, Point> {
  const target =
    elements.length === 1
      ? { x: 0, y: 0, w: resolution.width, h: resolution.height }
      : getBoundingBox(elements.map(({ box }) => box));

  if (!target) {
    return {};
  }

  return Object.fromEntries(
    elements.map(({ externalId, box }) => [
      externalId,
      getAlignOffset(box, target, alignType),
    ]),
  );
}

function getAlignOffset(box: Box, target: Box, alignType: AlignType): Point {
  const offsets: Record<AlignType, Point> = {
    left: { x: target.x - box.x, y: 0 },
    center: { x: target.x + (target.w - box.w) / 2 - box.x, y: 0 },
    right: { x: target.x + target.w - box.w - box.x, y: 0 },
    top: { x: 0, y: target.y - box.y },
    middle: { x: 0, y: target.y + (target.h - box.h) / 2 - box.y },
    bottom: { x: 0, y: target.y + target.h - box.h - box.y },
  };
  const { x, y } = offsets[alignType];

  return { x: Math.round(x), y: Math.round(y) };
}
